import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Clock,
  Tag as TagIcon,
  Check,
  Copy,
  Trash2,
} from 'lucide-react';

import MermaidRenderer from '../../components/docs/MermaidRenderer';
import { docsService } from '../../services/docs/docsService';
import type {
  DocsProcess,
  ProcessRevision,
  MermaidNodeBinding,
  DocsTag,
  DocsProcedure,
} from '../../services/docs/types';
import { useAuth } from '../../hooks/useAuth';
import { ADMIN_CLEARANCE_LEVEL } from '../../services/types';

const DocsProcessPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { clearanceLevel } = useAuth();
  const isAdmin =
    typeof clearanceLevel === 'number' &&
    clearanceLevel >= ADMIN_CLEARANCE_LEVEL;
  const [process, setProcess] = useState<DocsProcess | null>(null);
  const [revision, setRevision] = useState<ProcessRevision | null>(null);
  const [bindings, setBindings] = useState<MermaidNodeBinding[]>([]);
  const [tags, setTags] = useState<DocsTag[]>([]);
  const [childProcesses, setChildProcesses] = useState<DocsProcess[]>([]);
  const [linkedProcedures, setLinkedProcedures] = useState<DocsProcedure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!slug) return;

    const fetchProcess = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await docsService.getProcess(slug);
        setProcess(data.process);
        setRevision(data.revision);
        setBindings(data.bindings);
        setTags(data.tags);
        setChildProcesses(data.childProcesses);
        setLinkedProcedures(data.linkedProcedures);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load process');
      } finally {
        setLoading(false);
      }
    };

    fetchProcess();
  }, [slug]);

  const handlePublish = async () => {
    if (!process || !slug) return;
    setPublishing(true);
    try {
      await docsService.approveProcessRevision(process.id);
      const data = await docsService.getProcess(slug);
      setProcess(data.process);
      setRevision(data.revision);
      setBindings(data.bindings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleDelete = async () => {
    if (!process) return;
    if (!window.confirm(`Delete "${process.title}"? This cannot be undone.`))
      return;
    setDeleting(true);
    try {
      await docsService.deleteProcess(process.id);
      navigate('/docs');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
      setDeleting(false);
    }
  };

  const handleCopyId = () => {
    if (!process) return;
    navigator.clipboard.writeText(process.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-4 bg-gray-200 rounded w-2/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error || !process) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700">{error || 'Process not found'}</p>
          <button
            onClick={() => navigate('/docs')}
            className="mt-4 text-blue-600 hover:text-blue-800 text-sm"
          >
            Back to Docs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {process.title}
            </h1>
            {process.summary && (
              <p className="mt-1 text-gray-600">{process.summary}</p>
            )}
            <button
              onClick={handleCopyId}
              className="flex items-center gap-1 mt-1 text-xs text-gray-400 hover:text-gray-600 font-mono"
              title="Copy document ID"
            >
              <Copy className="w-3 h-3" />
              {copied ? 'Copied!' : process.id}
            </button>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && process.status !== 'published' && (
              <button
                onClick={handlePublish}
                disabled={publishing}
                className="flex items-center px-3 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-green-400"
              >
                <Check className="w-4 h-4 mr-1.5" />
                {publishing ? 'Publishing...' : 'Publish'}
              </button>
            )}
            <button
              onClick={() => navigate(`/docs/processes/${process.slug}/edit`)}
              className="flex items-center px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Edit className="w-4 h-4 mr-1.5" />
              Edit
            </button>
            {isAdmin && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center px-3 py-2 text-sm text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </div>

        {/* Status and meta */}
        <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              process.status === 'published'
                ? 'bg-green-100 text-green-800'
                : process.status === 'draft'
                  ? 'bg-yellow-100 text-yellow-800'
                  : process.status === 'archived'
                    ? 'bg-gray-100 text-gray-600'
                    : 'bg-blue-100 text-blue-800'
            }`}
          >
            {process.status.replace('_', ' ')}
          </span>
          {revision && (
            <span className="flex items-center">
              <Clock className="w-3.5 h-3.5 mr-1" />
              Rev {revision.revisionNumber || 'Draft'}
            </span>
          )}
          <span>
            Updated {new Date(process.updatedAt).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Mermaid diagram */}
      {revision && revision.mermaidSource && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <MermaidRenderer
            source={revision.mermaidSource}
            bindings={bindings}
          />
        </div>
      )}

      {/* Child processes */}
      {childProcesses.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Sub-processes
          </h2>
          <div className="grid gap-2">
            {childProcesses.map((child) => (
              <Link
                key={child.id}
                to={`/docs/processes/${child.slug}`}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
              >
                <span className="font-medium text-gray-900">{child.title}</span>
                {child.summary && (
                  <span className="text-sm text-gray-500 truncate ml-4 max-w-xs">
                    {child.summary}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Linked procedures */}
      {linkedProcedures.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Procedures
          </h2>
          <div className="grid gap-2">
            {linkedProcedures.map((proc) => (
              <Link
                key={proc.id}
                to={`/docs/procedures/${proc.slug}`}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
              >
                <div>
                  <span className="font-medium text-gray-900">
                    {proc.title}
                  </span>
                  <span className="ml-2 text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                    {proc.type.replace('_', ' ')}
                  </span>
                </div>
                {proc.summary && (
                  <span className="text-sm text-gray-500 truncate ml-4 max-w-xs">
                    {proc.summary}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Tags */}
      {tags.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <TagIcon className="w-4 h-4 text-gray-400" />
            {tags.map((tag) => (
              <span
                key={tag.id}
                className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocsProcessPage;
